import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import BrandManageModal from '@features/BrandManageModal';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import { MultiSelect } from '@components/MultiSelect';
import { useTranslation, useLocations } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { brandsActions } from '@actions';
import { FORM_TYPE } from '@constants/brands';

const Wrapper = styled('div')({
    display: 'flex',
    gap: 24,
    marginTop: -8,
    marginBottom: 8,
    '@media (max-width: 768px)': {
        marginTop: -16,
    },
});

const StyledRadioGroup = styled(RadioGroup)({
    flexDirection: 'row',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
        marginRight: 0,
        marginLeft: '-3px !important',
    },
});

export const LOCATION_TYPE = {
    LOCATIONS: '0',
    BRANDS: '1',
};

const ComboLocationMultiselect = ({
    brandsOptions,
    locationsOptions,
    data,
    disabled,
    isView,
    module,
    updatedLocationsCb = () => {},
}) => {
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const brands = useSelector(state => state.brands.data);
    const { control, watch, setValue } = useFormContext();
    const { t } = useTranslation();
    const { mapSelectedLocationsToFormValue, hasSingleLocation, singleLocationId, singleLocationFormValue } =
        useLocations();
    const dispatch = useDispatch();
    const { hasFullAccess, hasFullSettings, hasAnyBrandScope, getWritableLocationIds, getWritableBrandIds } =
        usePermissions();

    const canManageLocations = hasFullAccess('locations');

    const filteredLocationsOptions = useMemo(() => {
        if (!module || isView) return locationsOptions;
        const writableIds = getWritableLocationIds(module);
        if (writableIds === null) return locationsOptions;
        return locationsOptions.filter(loc => writableIds.has(loc.id));
    }, [locationsOptions, module, isView, getWritableLocationIds]);

    const filteredBrandsOptions = useMemo(() => {
        if (!module || isView) return brandsOptions;
        const writableIds = getWritableBrandIds(module);
        if (writableIds === null) return brandsOptions;
        return brandsOptions.filter(b => writableIds.has(b.value));
    }, [brandsOptions, module, isView, getWritableBrandIds]);

    const refetchBrandsCallback = () => dispatch(brandsActions.getAll());

    const autoSelectSingleLocation = useMemo(() => {
        if (!hasSingleLocation || !singleLocationId || isView) return false;
        return filteredLocationsOptions.some(loc => loc.id === singleLocationId);
    }, [hasSingleLocation, singleLocationId, isView, filteredLocationsOptions]);

    const locationType = watch('locationType');
    const brandAccess = watch('brandAccess');

    const hasBrandAccess = module ? filteredBrandsOptions.length > 0 : canManageLocations && hasAnyBrandScope;

    useEffect(() => {
        setValue('brandAccess', hasBrandAccess);

        if (autoSelectSingleLocation) {
            setValue('locationType', LOCATION_TYPE.LOCATIONS, { shouldValidate: true });
            setValue('locations', singleLocationFormValue, { shouldValidate: true });
            setValue('brands', [], { shouldValidate: true });
            setValue('selectedLocationsIds', [singleLocationId]);
            // On edit the entity already belongs to this one location, so skip the callback
            // that resets location-dependent fields — there's no real location change to react to.
            if (!data) updatedLocationsCb([singleLocationId]);
            return;
        }

        if (!hasBrandAccess && !isView) {
            setValue('locationType', LOCATION_TYPE.LOCATIONS, { shouldValidate: true });
        }

        if (data) {
            setValue('brands', data?.brands, { shouldValidate: true });
            setValue('locations', mapSelectedLocationsToFormValue(data?.locations), {
                shouldValidate: true,
            });
            setValue('locationType', data?.locations.length ? LOCATION_TYPE.LOCATIONS : LOCATION_TYPE.BRANDS, {
                shouldValidate: true,
            });
        }
    }, [setValue, hasBrandAccess, isView, data, autoSelectSingleLocation, singleLocationId, singleLocationFormValue]);

    if (autoSelectSingleLocation) return null;

    return brandAccess || isView ? (
        <div className="combo-location-multiselect">
            <Controller
                control={control}
                name="locationType"
                render={() => (
                    <Wrapper className="location-type">
                        {[
                            { id: LOCATION_TYPE.BRANDS, name: t('common.brands') },
                            { id: LOCATION_TYPE.LOCATIONS, name: t('common.locations') },
                        ].map(type => (
                            <Fragment key={type.id}>
                                <StyledRadioGroup
                                    value={locationType}
                                    onChange={e => {
                                        setValue('locationType', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                >
                                    <FormControlLabel
                                        value={type.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'var(--text-secondary)',
                                                    padding: '6px',
                                                    '&.Mui-checked': { color: 'var(--primary-main)' },
                                                }}
                                                disabled={disabled}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                {type.name}
                                            </Typography>
                                        }
                                        disabled={disabled}
                                    ></FormControlLabel>
                                </StyledRadioGroup>
                            </Fragment>
                        ))}
                    </Wrapper>
                )}
            />
            {locationType === LOCATION_TYPE.BRANDS && (
                <Controller
                    control={control}
                    name="brands"
                    render={({ field, fieldState: { error }, ref }) => (
                        <MultiSelect
                            {...field}
                            ref={ref}
                            onChange={value => {
                                setValue('brands', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                setValue('locations', [], { shouldDirty: true, shouldValidate: true });
                                const availableCompanies = brands
                                    .filter(item => value.includes(item.id))
                                    .map(item => item.locations)
                                    .flat()
                                    .map(item => item.id);
                                updatedLocationsCb(availableCompanies);
                                setValue('selectedLocationsIds', availableCompanies);
                            }}
                            options={filteredBrandsOptions}
                            label={t('common.brands')}
                            fullWidth
                            countable
                            required
                            error={error}
                            disabled={disabled}
                            creatable={hasFullSettings()}
                            creatableText={t('SettingsBrands.createNewBrand')}
                            creatableClick={() => setModal({ open: true, type: FORM_TYPE.NEW })}
                        />
                    )}
                />
            )}
            {locationType === LOCATION_TYPE.LOCATIONS && (
                <Controller
                    control={control}
                    name="locations"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Autocomplete
                            {...field}
                            ref={ref}
                            label={t('common.locations')}
                            options={filteredLocationsOptions}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{
                                style: {
                                    display: field?.value?.length > 0 ? 'none' : 'block',
                                },
                            }}
                            onChangeAutoComplete={items => {
                                setValue('locations', items.length ? items : [], {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setValue('brands', [], { shouldDirty: true, shouldValidate: true });
                                updatedLocationsCb(items.map(i => i.id));
                                setValue(
                                    'selectedLocationsIds',
                                    items.map(i => i.id),
                                );
                            }}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            error={error}
                            disabled={disabled}
                        />
                    )}
                />
            )}
            {modal.open ? (
                <BrandManageModal
                    modal={modal}
                    onClose={() => setModal({ open: false, type: FORM_TYPE.NEW })}
                    refetchCallback={refetchBrandsCallback}
                />
            ) : null}
        </div>
    ) : (
        <div className="combo-location-multiselect">
            <Controller
                control={control}
                name="locations"
                render={({ field, fieldState: { error }, ref }) => (
                    <Autocomplete
                        {...field}
                        ref={ref}
                        label={t('common.locations')}
                        options={filteredLocationsOptions}
                        value={field.value}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        inputProps={{
                            style: {
                                display: field?.value?.length > 0 ? 'none' : 'block',
                            },
                        }}
                        onChangeAutoComplete={items => {
                            setValue('locations', items.length ? items : [], {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            setValue('brands', [], { shouldDirty: true, shouldValidate: true });
                            updatedLocationsCb(items.map(i => i.id));
                            setValue(
                                'selectedLocationsIds',
                                items.map(i => i.id),
                            );
                        }}
                        groupMultiProps={{
                            getOptionLabel: option => option.label,
                        }}
                        required
                        error={error}
                        disabled={disabled}
                    />
                )}
            />
        </div>
    );
};

export default ComboLocationMultiselect;
